import { API_CONFIG, API_ENDPOINTS } from '../config/api';
import { useAuthStore } from '../stores/authStore';

/** SSE start 事件的归一化载荷（2026-09-17 友好性⑤⑥：生产同源直放时带定性/警告元数据） */
export interface StreamStartData {
    sessionId: string;
    sessionName: string;
    messageId: string;
    /** 生产后链定性（ACCEPT/DENY/REVIEW/NON_NGS/EMPTY），仅确定性回放携带 */
    verdict?: string;
    /** 复核/低质量警告（逐行） */
    review_note?: string;
    /** 融合伴侣提示 */
    partner_note?: string;
    /** 生产同源 finalOut 直放标记（未经 LLM 改写） */
    deterministic?: boolean;
}

/** SSE complete 事件的归一化载荷 */
export interface StreamCompleteData {
    sessionId: string;
    sessionName: string;
    messageId: string;    /** 后端检测到报告文本并已转入 Graph 提取流程 */
    reportDetected?: boolean;
    /** 虚拟文件 ID，用于后续挂载确认卡片 */
    reportFileId?: string;
}

export interface ApiResponse<T = any> {    data?: T;
    error?: string;
    status: number;
}

export class ApiError extends Error {
    constructor(
        message: string,
        public status: number,
        public response?: any
    ) {
        super(message);
        this.name = 'ApiError';
    }
}

class ApiClient {
    private baseURL: string;
    private timeout: number;
    private retryAttempts: number;
    private retryDelay: number;

    constructor() {
        this.baseURL = API_CONFIG.BASE_URL;
        this.timeout = API_CONFIG.TIMEOUT;
        this.retryAttempts = API_CONFIG.RETRY_ATTEMPTS;
        this.retryDelay = API_CONFIG.RETRY_DELAY;
    }

    private async delay(ms: number): Promise<void> {
        return new Promise(resolve => setTimeout(resolve, ms));
    }

    /**
     * 401 自动刷新 access token（单例防并发刷新）。
     * 成功返回新 token；refresh token 失效时登出并返回 null。
     * 注意：直接用 fetch 实现，不走 makeRequest，避免递归刷新。
     */
    private refreshPromise: Promise<string | null> | null = null;

    private async refreshAccessToken(): Promise<string | null> {
        if (!this.refreshPromise) {
            this.refreshPromise = this.performTokenRefresh();
        }
        try {
            return await this.refreshPromise;
        } finally {
            this.refreshPromise = null;
        }
    }

    private async performTokenRefresh(): Promise<string | null> {
        try {
            const { refreshToken, updateTokens, logout } = useAuthStore.getState();
            if (!refreshToken) return null;

            const resp = await fetch(`${this.baseURL}${API_ENDPOINTS.AUTH.REFRESH}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
                body: JSON.stringify({ refresh: refreshToken }),
            });

            if (!resp.ok) {
                console.warn('⚠️ Refresh token 已失效，执行登出');
                logout('登录状态已过期，请重新登录');
                return null;
            }

            const data = await resp.json();
            // simplejwt TokenRefreshView 返回 {access, refresh?}，无 expires_in——从 JWT 解码
            const newAccess: string | undefined = data.access ?? data.access_token;
            if (!newAccess) {
                logout('登录状态已过期，请重新登录');
                return null;
            }

            let expiresIn = 3600;
            try {
                const payload = JSON.parse(atob(newAccess.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
                if (payload.exp) expiresIn = payload.exp - Math.floor(Date.now() / 1000);
            } catch { /* 使用默认值 */ }

            updateTokens({
                access_token: newAccess,
                refresh_token: data.refresh ?? data.refresh_token,
                expires_in: expiresIn,
            });
            console.log('🔄 Access token 已自动刷新');
            return newAccess;
        } catch (e) {
            console.error('Token 刷新异常:', e);
            return null;
        }
    }

    private getAuthHeaders(authString?: string): Record<string, string> {
        const headers: Record<string, string> = {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
        };

        if (authString) {
            // 支持JWT Bearer Token和Basic Auth
            if (authString.startsWith('Bearer ')) {
                headers['Authorization'] = authString;
            } else if (authString.startsWith('Basic ')) {
                headers['Authorization'] = authString;
            } else {
                // 默认为Basic Auth格式
                headers['Authorization'] = `Basic ${authString}`;
            }
        }

        return headers;
    }

    private async fetchWithTimeout(
        url: string,
        options: RequestInit,
        timeout: number
    ): Promise<Response> {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), timeout);

        try {
            const response = await fetch(url, {
                ...options,
                signal: controller.signal,
            });
            clearTimeout(timeoutId);
            return response;
        } catch (error) {
            clearTimeout(timeoutId);
            throw error;
        }
    }

    private async makeRequest<T>(
        endpoint: string,
        options: RequestInit = {},
        authString?: string,
        attempt = 1,
        hasRefreshed = false
    ): Promise<ApiResponse<T>> {
        const url = `${this.baseURL}${endpoint}`;
        const headers = {
            ...this.getAuthHeaders(authString),
            ...options.headers,
        };

        try {
            const response = await this.fetchWithTimeout(
                url,
                { ...options, headers },
                this.timeout
            );

            // ✅ 401 自动刷新 token 并重试一次
            if (response.status === 401 && authString && !hasRefreshed) {
                const newToken = await this.refreshAccessToken();
                if (newToken) {
                    return this.makeRequest<T>(endpoint, options, `Bearer ${newToken}`, attempt, true);
                }
            }

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                const errorMessage = errorData.detail || errorData.message || errorData.error || `HTTP ${response.status}`;

                console.error(`API Error [${response.status}] ${url}:`, errorData);

                throw new ApiError(
                    errorMessage,
                    response.status,
                    errorData
                );
            }

            // 处理204 No Content响应（删除操作通常返回204）
            if (response.status === 204) {
                return { data: null as any, status: response.status };
            }

            const data = await response.json().catch(() => null);
            return { data, status: response.status };
        } catch (error) {
            // 重试逻辑
            if (
                attempt < this.retryAttempts &&
                (error instanceof TypeError || // 网络错误
                    (error instanceof ApiError && error.status >= 500)) // 服务器错误
            ) {
                await this.delay(this.retryDelay * attempt);
                return this.makeRequest(endpoint, options, authString, attempt + 1);
            }

            if (error instanceof ApiError) {
                return { error: error.message, status: error.status };
            }

            return {
                error: error instanceof Error ? error.message : 'Unknown error',
                status: 0,
            };
        }
    }

    async get<T>(
        endpoint: string,
        authString?: string
    ): Promise<ApiResponse<T>> {
        return this.makeRequest<T>(endpoint, { method: 'GET' }, authString);
    }

    async post<T>(
        endpoint: string,
        data?: any,
        authString?: string
    ): Promise<ApiResponse<T>> {
        return this.makeRequest<T>(
            endpoint,
            {
                method: 'POST',
                body: data ? JSON.stringify(data) : undefined,
            },
            authString
        );
    }

    async put<T>(
        endpoint: string,
        data?: any,
        authString?: string
    ): Promise<ApiResponse<T>> {
        return this.makeRequest<T>(
            endpoint,
            {
                method: 'PUT',
                body: data ? JSON.stringify(data) : undefined,
            },
            authString
        );
    }

    async patch<T>(
        endpoint: string,
        data?: any,
        authString?: string
    ): Promise<ApiResponse<T>> {
        return this.makeRequest<T>(
            endpoint,
            {
                method: 'PATCH',
                body: data ? JSON.stringify(data) : undefined,
            },
            authString
        );
    }

    async delete<T>(
        endpoint: string,
        authString?: string
    ): Promise<ApiResponse<T>> {
        return this.makeRequest<T>(endpoint, { method: 'DELETE' }, authString);
    }

    async uploadFile<T>(
        endpoint: string,
        formData: FormData,
        authString?: string,
        _onProgress?: (progress: number) => void,
        hasRefreshed = false
    ): Promise<ApiResponse<T>> {
        const url = `${this.baseURL}${endpoint}`;
        const headers: Record<string, string> = {};

        if (authString) {
            // 支持JWT Bearer Token和Basic Auth
            if (authString.startsWith('Bearer ') || authString.startsWith('Basic ')) {
                headers['Authorization'] = authString;
            } else {
                headers['Authorization'] = `Basic ${authString}`;
            }
        }

        // 文件上传使用更长的超时时间（5分钟）
        const uploadTimeout = 300000; // 5分钟

        try {
            const response = await this.fetchWithTimeout(
                url,
                {
                    method: 'POST',
                    headers,
                    body: formData,
                },
                uploadTimeout
            );

            // ✅ 401 自动刷新 token 并重试一次
            if (response.status === 401 && authString && !hasRefreshed) {
                const newToken = await this.refreshAccessToken();
                if (newToken) {
                    return this.uploadFile<T>(endpoint, formData, `Bearer ${newToken}`, _onProgress, true);
                }
            }

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new ApiError(
                    errorData.detail || errorData.message || `HTTP ${response.status}`,
                    response.status,
                    errorData
                );
            }

            const data = await response.json();
            return { data, status: response.status };
        } catch (error) {
            if (error instanceof ApiError) {
                return { error: error.message, status: error.status };
            }

            return {
                error: error instanceof Error ? error.message : 'Upload failed',
                status: 0,
            };
        }
    }

    async streamRequest(
        endpoint: string,
        data: any,
        authString?: string,
        onChunk?: (chunk: string) => void,
        onStart?: (data: StreamStartData) => void,
        onComplete?: (data: StreamCompleteData) => void,
        hasRefreshed = false
    ): Promise<ApiResponse<void>> {
        const url = `${this.baseURL}${endpoint}`;
        const headers = this.getAuthHeaders(authString);

        // 空闲超时：超过该时间没有任何数据到达则中断请求，避免 UI 永久卡在"正在输入"
        const controller = new AbortController();
        let idleTimer: ReturnType<typeof setTimeout> | null = null;
        const resetIdleTimer = () => {
            if (idleTimer) clearTimeout(idleTimer);
            idleTimer = setTimeout(() => controller.abort(), this.timeout);
        };

        try {
            resetIdleTimer();
            const response = await fetch(url, {
                method: 'POST',
                headers,
                body: JSON.stringify(data),
                signal: controller.signal,
            });

            // ✅ 401 自动刷新 token 并重试一次
            if (response.status === 401 && authString && !hasRefreshed) {
                const newToken = await this.refreshAccessToken();
                if (newToken) {
                    return this.streamRequest(endpoint, data, `Bearer ${newToken}`, onChunk, onStart, onComplete, true);
                }
            }

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new ApiError(
                    errorData.detail || errorData.message || `HTTP ${response.status}`,
                    response.status,
                    errorData
                );
            }


            if (!response.body) {
                throw new Error('No response body for streaming');
            }

            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            let leftover = ''; // 用于存放不完整的行
            let streamError: string | null = null; // 记录流内 error 事件（不能在被捕获的 try 里 throw）
            // eslint-disable-next-line no-constant-condition
            while (true) {
                const { done, value } = await reader.read();
                if (done) break;

                resetIdleTimer(); // 有数据到达，重置空闲计时
                const chunk = decoder.decode(value, { stream: true });
                const combined = leftover + chunk;
                const lines = combined.split('\n');
                // 留下最后一行，因为它可能是不完整的
                leftover = lines.pop() || '';

                for (const line of lines) {
                    const trimmedLine = line.trim();
                    if (!trimmedLine || !trimmedLine.startsWith('data: ')) continue;

                    try {
                        const jsonStr = trimmedLine.substring(6);
                        const data = JSON.parse(jsonStr);
                        if (data.type === 'start' && onStart) {
                            onStart({
                                sessionId: data.session_id,
                                sessionName: data.session_name || '',
                                messageId: data.message_id,
                                // 确定性回放附带的定性/警告元数据（友好性⑤⑥ → VerdictCard）
                                verdict: data.verdict,
                                review_note: data.review_note,
                                partner_note: data.partner_note,
                                deterministic: !!data.deterministic,
                            });
                        } else if (data.type === 'chunk' && data.content && onChunk) {
                            // 直接传递每个 chunk，不累积
                            onChunk(data.content);
                        } else if (data.type === 'complete') {
                            // 处理完成事件（可能包含会话标题更新 / 报告文本检测结果）
                            if (onComplete) {
                                onComplete({
                                    sessionId: data.session_id,
                                    sessionName: data.session_name || '',
                                    messageId: data.message_id,
                                    reportDetected: !!data.report_detected,
                                    reportFileId: data.report_file_id,
                                });
                            }
                        } else if (data.type === 'error') {
                            // 记录错误，在读取循环结束后统一抛出
                            streamError = data.error || 'Stream error';
                        }
                    } catch (e) {
                        console.error('Failed to parse stream data:', e);
                    }

                }
            }

            if (streamError) {
                throw new Error(streamError);
            }

            return { status: response.status };
        } catch (error) {
            if (error instanceof ApiError) {
                return { error: error.message, status: error.status };
            }

            if (error instanceof Error && error.name === 'AbortError') {
                return { error: '响应超时，请检查网络后重试', status: 0 };
            }

            return {
                error: error instanceof Error ? error.message : 'Stream failed',
                status: 0,
            };
        } finally {
            if (idleTimer) clearTimeout(idleTimer);
        }
    }
}

export const apiClient = new ApiClient();